
import React, { useState } from "react";
import { UserPlus, UsersIcon, Building2, Landmark, Globe, User, LogIn, Heart, Plus, FileText, Menu, X, Home, Package, Users, Mic } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { ShortRegistrationForm } from "@/components/forms/ShortRegistrationForm";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Link, useNavigate } from "react-router-dom";
import { NotificationsDropdown } from "@/components/notifications/NotificationsDropdown";
import { useCategories } from "@/hooks/useCategories";
import logo from "@/assets/logo.png";

interface NavigationProps {
  isRTL: boolean;
  setRegistrationType: (type: string) => void;
  handleRegistrationClick: () => void;
}

export const Navigation = ({ isRTL, setRegistrationType, handleRegistrationClick }: NavigationProps) => {
  const { toast } = useToast();
  const { i18n } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { visibleCategories } = useCategories();

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === "en" ? "ar" : "en");
  };

  const handleRegistrationSelect = (type: string) => {
    console.log("Registration type selected:", type);
    setSelectedType(type);
    setRegistrationType(type);
    setIsDialogOpen(true);
  };

  const handleLogout = async () => {
    await signOut();
    toast({
      title: isRTL ? "تم تسجيل الخروج" : "Logged out",
      description: isRTL ? "تم تسجيل خروجك بنجاح" : "You have been logged out successfully",
    });
  };

  const getIconComponent = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    return IconComponent || LucideIcons.Calendar;
  };

  const handleMobileNavigation = (path: string) => {
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <header className="sticky top-0 z-50 relative overflow-hidden" role="banner">
      {/* Skip to main content link for keyboard/screen-reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-white focus:text-primary focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {isRTL ? "تخطي إلى المحتوى الرئيسي" : "Skip to main content"}
      </a>
      {/* Gradient Background - matching Footer */}
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-[#1a0f22] via-[#241530] to-[#1a0f22]" />
      
      {/* Decorative Pattern - matching Footer */}
      <div aria-hidden="true" className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="navPattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M30 0L60 30L30 60L0 30Z" fill="none" stroke="white" strokeWidth="0.5"/>
              <circle cx="30" cy="30" r="10" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#navPattern)" />
        </svg>
      </div>

      {/* Glowing Orbs - subtle */}
      <div aria-hidden="true" className="absolute top-0 left-1/4 w-48 h-48 bg-primary/20 rounded-full blur-3xl" />
      <div aria-hidden="true" className="absolute bottom-0 right-1/4 w-32 h-32 bg-gold/10 rounded-full blur-3xl" />

      <nav
        aria-label={isRTL ? "التنقل الرئيسي" : "Main navigation"}
        className="relative z-10 container py-3 sm:py-4 px-3 sm:px-4 border-b border-white/10"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Mobile Menu Button */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="sm:hidden p-1.5 h-8 w-8 text-white hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white"
                  aria-label={isRTL ? "فتح القائمة" : "Open menu"}
                  aria-expanded={isMobileMenuOpen}
                  aria-controls="mobile-navigation-menu"
                >
                  <Menu className="w-5 h-5" aria-hidden="true" />
                </Button>
              </SheetTrigger>
              <SheetContent
                id="mobile-navigation-menu"
                side={isRTL ? "right" : "left"}
                className="w-[270px] p-0"
                aria-label={isRTL ? "قائمة التنقل المحمولة" : "Mobile navigation menu"}
              >
                <SheetHeader className="px-3 py-1.5 border-b bg-gradient-to-r from-primary to-primary-400">
                  <SheetTitle className="text-white flex items-center gap-2">
                    <img src={logo} alt={isRTL ? "شعار دليل يدل" : "Yadel logo"} className="h-7 w-auto" />
                  </SheetTitle>
                </SheetHeader>
                
                <div className="flex flex-col" role="menu">
                  {/* Home */}
                  <button
                    role="menuitem"
                    onClick={() => handleMobileNavigation("/")}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors"
                  >
                    <Home className="w-4 h-4 text-primary flex-shrink-0" aria-hidden="true" />
                    <span className="text-[13px] font-medium">{isRTL ? "الرئيسية" : "Home"}</span>
                  </button>

                  {/* Dynamic Categories */}
                  <div className="px-3 py-0.5">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      {isRTL ? "الأقسام" : "Categories"}
                    </span>
                  </div>
                  {visibleCategories.map((category) => {
                    const IconComponent = getIconComponent(category.icon);
                    return (
                      <button
                        key={category.id}
                        role="menuitem"
                        onClick={() => handleMobileNavigation(category.path)}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors"
                      >
                        <IconComponent className={`w-4 h-4 flex-shrink-0 ${category.color}`} aria-hidden="true" />
                        <span className="text-[13px] font-medium">
                          {isRTL ? category.name_ar : category.name_en}
                        </span>
                      </button>
                    );
                  })}

                  {/* Divider */}
                  <div className="my-0.5 border-t" />

                  {/* Quick Links */}
                  <div className="px-3 py-0.5">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      {isRTL ? "روابط سريعة" : "Quick Links"}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => handleMobileNavigation("/packages")}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 transition-colors"
                  >
                    <Package className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    <span className="text-[13px] font-medium">{isRTL ? "أضف فعاليتك" : "Add Your Event"}</span>
                  </button>

                  <button
                    onClick={() => handleMobileNavigation("/social-groups")}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 transition-colors"
                  >
                    <Users className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span className="text-[13px] font-medium">{isRTL ? "المجموعات" : "Social Groups"}</span>
                  </button>

                  <button
                    onClick={() => handleMobileNavigation("/x-spaces")}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 transition-colors"
                  >
                    <Mic className="w-4 h-4 text-gray-800 flex-shrink-0" />
                    <span className="text-[13px] font-medium">{isRTL ? "X Spaces" : "X Spaces"}</span>
                  </button>

                  {/* Auth Section */}
                  <div className="my-0.5 border-t" />
                  
                  {user ? (
                    <>
                      <button
                        onClick={() => handleMobileNavigation("/profile")}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 transition-colors"
                      >
                        <User className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="text-[13px] font-medium">{isRTL ? "الملف الشخصي" : "Profile"}</span>
                      </button>
                      <button
                        onClick={() => handleMobileNavigation("/my-submissions")}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 transition-colors"
                      >
                        <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="text-[13px] font-medium">{isRTL ? "طلباتي" : "My Submissions"}</span>
                      </button>
                      <button
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 hover:bg-red-50 transition-colors text-red-600"
                      >
                        <LogIn className="w-4 h-4 flex-shrink-0" />
                        <span className="text-[13px] font-medium">{isRTL ? "تسجيل الخروج" : "Logout"}</span>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleMobileNavigation("/auth")}
                      className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 transition-colors"
                    >
                      <LogIn className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-[13px] font-medium">{isRTL ? "تسجيل الدخول" : "Login"}</span>
                    </button>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            <Link
              to="/"
              className="flex items-center gap-3 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0f22]"
              aria-label={isRTL ? "دليل يدل - العودة إلى الصفحة الرئيسية" : "Yadel - Go to homepage"}
            >
              <div className="bg-white rounded-2xl p-1.5 shadow-lg">
                <img src={logo} alt="دليل يدل - Yadel" className="h-14 sm:h-16 md:h-20 lg:h-24 w-auto object-contain" />
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3">
            <Link to="/packages" className="hidden sm:block">
              <Button 
                size="sm" 
                className="bg-gradient-to-r from-secondary to-gold hover:from-secondary/90 hover:to-gold/90 text-white gap-1.5 px-6 h-9 text-sm font-semibold rounded-xl transition-all shadow-sm shadow-gold/20 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0f22]"
              >
                <Plus className="w-4 h-4" aria-hidden="true" />
                {isRTL ? "أضف فعاليتك" : "Add Your Event"}
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleLanguage}
              aria-label={isRTL ? "تغيير اللغة إلى الإنجليزية" : "Switch language to Arabic"}
              className="flex items-center gap-1 sm:gap-2 bg-transparent border-white/60 text-white hover:bg-white/10 px-2 sm:px-3 h-8 sm:h-9 text-xs sm:text-sm focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0f22]"
            >
              <Globe className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
              <span className="hidden sm:inline">{isRTL ? "English" : "العربية"}</span>
              <span className="sm:hidden">{isRTL ? "EN" : "ع"}</span>
            </Button>

            {user ? (
              // User is logged in - show notifications and profile dropdown
              <>
                <NotificationsDropdown isRTL={isRTL} />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline"
                    size="sm"
                    className="hidden sm:flex items-center gap-2 bg-transparent border-white/60 text-white hover:bg-white/10 shadow-md px-4 h-9 text-sm focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0f22]"
                    aria-label={isRTL ? "قائمة الحساب" : "Account menu"}
                  >
                    <User className="w-4 h-4" aria-hidden="true" />
                    {isRTL ? "حسابي" : "My Account"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white rounded-lg shadow-xl border-0">
                  <DropdownMenuItem 
                    onClick={() => navigate("/profile")}
                    className="gap-2 hover:bg-gray-50 cursor-pointer focus:bg-gray-100"
                  >
                    <User className="w-4 h-4 text-[#0056b3]" />
                    {isRTL ? "الملف الشخصي" : "Profile"}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate("/my-submissions")}
                    className="gap-2 hover:bg-gray-50 cursor-pointer focus:bg-gray-100"
                  >
                    <FileText className="w-4 h-4 text-[#0056b3]" />
                    {isRTL ? "طلباتي" : "My Submissions"}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate("/profile")}
                    className="gap-2 hover:bg-gray-50 cursor-pointer focus:bg-gray-100"
                  >
                    <Heart className="w-4 h-4 text-[#0056b3]" />
                    {isRTL ? "المفضلة" : "Favorites"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="gap-2 hover:bg-red-50 cursor-pointer focus:bg-red-100 text-red-600"
                  >
                    <LogIn className="w-4 h-4" />
                    {isRTL ? "تسجيل الخروج" : "Logout"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              // User is not logged in - show login button (desktop only)
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/auth")}
                className="hidden sm:flex items-center gap-2 bg-transparent border-white/60 text-white hover:bg-white/10 px-3 h-9 text-sm focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a0f22]"
                aria-label={isRTL ? "تسجيل الدخول إلى الحساب" : "Sign in to your account"}
              >
                <LogIn className="w-4 h-4" aria-hidden="true" />
                {isRTL ? "تسجيل الدخول" : "Login"}
              </Button>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};
